import { useCallback, useEffect, useMemo, useState } from "react";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import QuickFiltersBar from "../../components/Organismos/Table/QuickFiltersBar";
import {
  buildFirmModificationLogColumnsDef,
  buildFirmModificationLogFiltersData,
  formatFirmModificationLogExcelRows,
  formatFirmModificationLogRows,
} from "./Logs/auditLogTableConfig";
import {
  buildFirmAccessLogColumnsDef,
  buildFirmAccessLogFiltersData,
  buildFirmLogPersistentFilter,
  FIRM_LOG_QUICK_FILTERS,
  formatFirmAccessLogRows,
  getFirmLogEndpoint,
  getFirmLogExcelFilename,
  isFirmLogModeKey,
  type FirmLogMode,
  type FirmLogOfficeScope,
} from "./Logs/firmLogsUtils";

/** Isolated table-preferences key for the firm logs modal (must not use `/ManageFirm`). */
export const FIRM_LOGS_TABLE_KEY = "/ManageFirm/firm-logs";
const MODIFICATION_LOG_COLUMNS = buildFirmModificationLogColumnsDef();
const ACCESS_LOG_COLUMNS = buildFirmAccessLogColumnsDef();

export type FirmLogsProps = {
  firmId: string;
  firmName?: string;
  officeScope?: FirmLogOfficeScope;
  embeddedInScrollContainer?: boolean;
};

const FirmLogs = ({
  firmId,
  firmName,
  officeScope,
  embeddedInScrollContainer = false,
}: FirmLogsProps) => {
  const [logMode, setLogMode] = useState<FirmLogMode>("modificationLog");
  const [tableReady, setTableReady] = useState(!embeddedInScrollContainer);

  useEffect(() => {
    if (!embeddedInScrollContainer) {
      return;
    }
    const frameId = window.requestAnimationFrame(() => {
      setTableReady(true);
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [embeddedInScrollContainer, firmId, officeScope]);

  const isModificationLog = logMode === "modificationLog";
  const endpoint = getFirmLogEndpoint(logMode);

  const persistentFilter = useMemo(
    () => buildFirmLogPersistentFilter(firmId, logMode, { officeScope }),
    [firmId, logMode, officeScope],
  );

  const filtersData = useMemo(
    () =>
      isModificationLog
        ? buildFirmModificationLogFiltersData(embeddedInScrollContainer)
        : buildFirmAccessLogFiltersData(
            embeddedInScrollContainer,
            logMode === "dataAdminLogin"
              ? "Data admin log date"
              : "Professional login date",
          ),
    [isModificationLog, embeddedInScrollContainer, logMode],
  );

  const columnsDef = useMemo(
    () => (isModificationLog ? MODIFICATION_LOG_COLUMNS : ACCESS_LOG_COLUMNS),
    [isModificationLog],
  );

  const handleQuickFilterClick = useCallback((key: string) => {
    if (isFirmLogModeKey(key)) {
      setLogMode(key);
    }
  }, []);

  const tableKey = `${firmId}-${logMode}-${officeScope?.kind ?? "all"}-${
    officeScope && officeScope.kind !== "parent" ? officeScope.officeId : ""
  }`;

  const searchPlaceholder = useMemo(() => {
    if (isModificationLog) {
      return "Search by action, entity, user, source, record ID or field values";
    }
    return "Search by user, email, action or role";
  }, [isModificationLog]);

  return (
    <div
      className={
        embeddedInScrollContainer
          ? "flex min-h-0 flex-1 w-full min-w-0 flex-col"
          : "flex flex-col gap-5"
      }
    >
      <div
        className={
          embeddedInScrollContainer ? "mb-5 w-full shrink-0 pt-2" : ""
        }
      >
        <QuickFiltersBar
          quickFilters={FIRM_LOG_QUICK_FILTERS}
          selectedQuickFilter={logMode}
          quickFilterTotalCount={{}}
          onQuickFilterClick={handleQuickFilterClick}
          lockActiveQuickFilter
          showCounts={false}
          variant={embeddedInScrollContainer ? "firmLogs" : "default"}
          roundedTop={false}
        />
      </div>
      {tableReady ? (
      <FilteredTableCore
        key={tableKey}
        columnsDef={columnsDef}
        endpoint={endpoint}
        filtersData={filtersData}
        persistentFilter={persistentFilter}
        defaultSorting={{ field: "timestamp", order: "desc" }}
        searchPlaceholder={searchPlaceholder}
        showSearchInput
        persistFilters={false}
        persistPageSize={false}
        persistSorting={false}
        tableKey={FIRM_LOGS_TABLE_KEY}
        showRowNumber
        showColumnVisibility={false}
        filtersPanelLayout={
          embeddedInScrollContainer ? "firmLogs" : "default"
        }
        formatExcelData={
          isModificationLog
            ? (data) => formatFirmModificationLogExcelRows(data, firmName)
            : undefined
        }
        formatData={
          isModificationLog
            ? (data) => formatFirmModificationLogRows(data, firmName)
            : formatFirmAccessLogRows
        }
        excelFilename={getFirmLogExcelFilename(firmName, logMode)}
        emptyStateMessage={
          isModificationLog
            ? "No audit records for the selected date range."
            : "No access records for the selected date range."
        }
        embeddedInScrollContainer={embeddedInScrollContainer}
        containerclassName={
          embeddedInScrollContainer ? "!w-full !px-0 !pt-0 !pb-0" : "!px-10 !pt-4"
        }
      />
      ) : (
        <div
          className="min-h-[12rem] w-full animate-pulse rounded-md bg-gray-100"
          aria-hidden
        />
      )}
    </div>
  );
};

export default FirmLogs;
